import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import {
  ConfirmationAuthResponse,
  ConfirmationAuthConfirmRequest,
  ConfirmationAuthConfirmResponse,
} from '@dreamai/types';
import ConfirmationSpecialView, {
  hasSpecialView,
  getActionTitle,
} from './ConfirmationSpecialView';
import './confirm-action.scss';

const ConfirmAction: React.FC = () => {
  const { confirmationCode } = useParams<{ confirmationCode: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [confirmationData, setConfirmationData] = useState<
    ConfirmationAuthResponse['confirmation'] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionTaken, setActionTaken] = useState<boolean>(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingAction, setProcessingAction] = useState<
    'confirm' | 'decline' | null
  >(null);

  useEffect(() => {
    const fetchConfirmationData = async () => {
      if (!confirmationCode) {
        setError('You must follow the link provided by Dream.');
        setLoading(false);
        return;
      }

      try {
        const accessToken = await getAccessTokenSilently();
        const response = await api.get<ConfirmationAuthResponse>(
          `/confirmation-auth/${confirmationCode}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.data.success && response.data.confirmation) {
          setConfirmationData(response.data.confirmation);
        } else {
          setError(response.data.error || 'Confirmation not found');
        }
      } catch (err: any) {
        setError(
          err.response?.data?.error || 'Failed to load confirmation data',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchConfirmationData();
  }, [confirmationCode, getAccessTokenSilently]);

  const handleAction = async (confirm: boolean) => {
    if (!confirmationCode || actionTaken || !confirmationData) return;

    try {
      setIsProcessing(true);
      setProcessingAction(confirm ? 'confirm' : 'decline');
      setActionTaken(true);
      const accessToken = await getAccessTokenSilently();
      const requestData: ConfirmationAuthConfirmRequest = { confirm };
      const response = await api.post<ConfirmationAuthConfirmResponse>(
        `/confirmation-auth/${confirmationCode}/confirm`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data.success) {
        setActionResult(response.data.message);
      } else {
        setActionResult(
          response.data.error ||
            'Failed to process your request. Please try again.',
        );
      }
    } catch (err: any) {
      setActionResult(
        err.response?.data?.error || 'An error occurred. Please try again.',
      );
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  if (loading) {
    return (
      <div className='confirm-action-container'>
        <div className='confirm-action-card'>
          <div className='loading-spinner'></div>
          <p>Loading confirmation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='confirm-action-container'>
        <div className='confirm-action-card error'>
          <h2>Error</h2>
          <p>{error || 'An error occurred. Please try again.'}</p>
        </div>
      </div>
    );
  }

  if (!confirmationData) {
    return (
      <div className='confirm-action-container'>
        <div className='confirm-action-card error'>
          <h2>Not Found</h2>
          <p>Confirmation request not found.</p>
        </div>
      </div>
    );
  }

  if (actionResult) {
    const isSuccess = actionResult.includes('successfully');
    return (
      <div className='confirm-action-container'>
        <div
          className={`confirm-action-card ${isSuccess ? 'success' : 'error'}`}
        >
          {isSuccess && (
            <div className='success-icon'>
              <svg
                width='64'
                height='64'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle
                  cx='12'
                  cy='12'
                  r='10'
                  fill='var(--Light-Green, rgba(89, 173, 162, 1))'
                />
                <path
                  d='M9 12l2 2 4-4'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          )}
          <h2>{isSuccess ? 'Complete' : 'Declined'}</h2>
          <p>{actionResult}</p>
        </div>
      </div>
    );
  }

  if (confirmationData.status !== 'PENDING') {
    const statusMessage =
      confirmationData.status === 'CONFIRMED'
        ? 'This action has already been confirmed.'
        : confirmationData.status === 'CANCELLED'
          ? 'This action has already been declined.'
          : 'This action has already been processed.';

    return (
      <div className='confirm-action-container'>
        <div className='confirm-action-card info'>
          <h2>Already Processed</h2>
          <p>{statusMessage}</p>
        </div>
      </div>
    );
  }

  // Check if this method has a special view
  if (hasSpecialView(confirmationData.method_name)) {
    return (
      <div className='confirm-action-container'>
        <div className='special-confirm-view'>
          <div className='confirm-banner'>
            <h2>{getActionTitle(confirmationData.method_name)}</h2>
            <div className='action-buttons'>
              <button
                className='btn btn-confirm'
                onClick={() => handleAction(true)}
                disabled={actionTaken || isProcessing}
              >
                {processingAction === 'confirm' ? (
                  <>
                    <span className='button-spinner'></span>
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
              <button
                className='btn btn-decline'
                onClick={() => handleAction(false)}
                disabled={actionTaken || isProcessing}
              >
                {processingAction === 'decline' ? (
                  <>
                    <span className='button-spinner'></span>
                    Processing...
                  </>
                ) : (
                  'Decline'
                )}
              </button>
            </div>
          </div>
          <div className='special-view-wrapper'>
            <ConfirmationSpecialView
              methodName={confirmationData.method_name}
              methodInput={confirmationData.method_input}
            />
          </div>
        </div>
      </div>
    );
  }

  // Standard view for methods without special views
  return (
    <div className='confirm-action-container'>
      <div className='confirm-action-card'>
        <h2>{getActionTitle(confirmationData.method_name)}</h2>
        <div className='task-description'>
          <p>{confirmationData.task}</p>
        </div>

        <div className='action-buttons'>
          <button
            className='btn btn-confirm'
            onClick={() => handleAction(true)}
            disabled={actionTaken || isProcessing}
          >
            {processingAction === 'confirm' ? (
              <>
                <span className='button-spinner'></span>
                Processing...
              </>
            ) : (
              'Confirm'
            )}
          </button>
          <button
            className='btn btn-decline'
            onClick={() => handleAction(false)}
            disabled={actionTaken || isProcessing}
          >
            {processingAction === 'decline' ? (
              <>
                <span className='button-spinner'></span>
                Processing...
              </>
            ) : (
              'Decline'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAction;
